import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calculator, Sparkles, CheckCircle, Calendar as CalendarIcon, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RV_TYPES = {
  'Travel Trailer': {
    minLength: 15,
    maxLength: 30,
    minPrice: 200,
    maxPrice: 300,
    icon: '🚐'
  },
  '5th Wheel': {
    minLength: 20,
    maxLength: 45,
    minPrice: 400,
    maxPrice: 500,
    icon: '🚛'
  },
  'Motorhome': {
    minLength: 30,
    maxLength: 45,
    minPrice: 500,
    maxPrice: 700,
    icon: '🚌'
  }
};

const SERVICES = [
  'Deep Scrub & Wash',
  'High-Gloss Wax & Polish',
  'Premium Protective Sealant',
  'Spot Free Shine Every Time'
];

export const QuoteCalculator = () => {
  const [rvType, setRvType] = useState('');
  const [rvLength, setRvLength] = useState('');
  const [quote, setQuote] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  
  // Booking form states
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    date: null,
    timeSlot: '',
    notes: ''
  });
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculateQuote = () => {
    if (!rvType || !rvLength) return;

    const typeData = RV_TYPES[rvType];
    const length = parseFloat(rvLength);

    // Validate length is within range
    if (length < typeData.minLength || length > typeData.maxLength) {
      setQuote({
        error: `Please enter a length between ${typeData.minLength}-${typeData.maxLength} ft for ${rvType}`
      });
      setShowQuote(true);
      return;
    }

    // Calculate price based on linear interpolation within the range
    const lengthRatio = (length - typeData.minLength) / (typeData.maxLength - typeData.minLength);
    const calculatedPrice = typeData.minPrice + (lengthRatio * (typeData.maxPrice - typeData.minPrice));
    
    setQuote({
      rvType,
      length,
      price: Math.round(calculatedPrice),
      priceRange: `$${typeData.minPrice}-$${typeData.maxPrice}`,
      services: SERVICES
    });
    setShowQuote(true);
  };

  useEffect(() => {
    if (rvType && rvLength) {
      calculateQuote();
    } else {
      setShowQuote(false);
    }
  }, [rvType, rvLength]);

  const handleGetQuote = (e) => {
    e.preventDefault();
    calculateQuote();
  };

  const handleBookNow = () => {
    setShowBookingForm(true);
    setBookingSubmitted(false);
    setBookingError('');
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setBookingError('');

    try {
      const bookingPayload = {
        // Customer info
        customerName: bookingData.name,
        customerEmail: bookingData.email,
        customerPhone: bookingData.phone,
        serviceAddress: `${bookingData.address}, ${bookingData.city}`,
        
        // RV details
        rvType: quote.rvType,
        rvLength: quote.length,
        estimatedPrice: quote.price,
        
        // Scheduling
        preferredDate: bookingData.date ? format(bookingData.date, 'yyyy-MM-dd') : null,
        preferredTime: bookingData.timeSlot,
        specialRequests: bookingData.notes,
        
        // Metadata
        status: 'pending',
        source: 'website_quote_calculator'
      };

      const response = await axios.post(`${API}/bookings`, bookingPayload);
      
      if (response.data) {
        setBookingSubmitted(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setShowBookingForm(false);
          setBookingData({
            name: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            date: null,
            timeSlot: '',
            notes: ''
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Booking error:', error);
      setBookingError('Something went wrong. Please call us at (574) 322-5652 to book directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '8:00 AM - 10:00 AM',
    '10:00 AM - 12:00 PM',
    '12:00 PM - 2:00 PM',
    '2:00 PM - 4:00 PM',
    '4:00 PM - 6:00 PM'
  ];

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mb-4">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Get Your Instant Quote
        </h2>
        <p className="text-lg text-gray-600">
          Simple, flat-fee pricing • Professional mobile service
        </p>
      </div>

      <Card className="shadow-xl border-2 border-gray-100">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
          <CardTitle className="text-2xl">RV Details</CardTitle>
          <CardDescription className="text-base">
            Tell us about your RV to get an accurate price estimate
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleGetQuote} className="space-y-6">
            {/* RV Type Selection */}
            <div className="space-y-2">
              <Label htmlFor="rv-type" className="text-base font-semibold">
                RV Type
              </Label>
              <Select value={rvType} onValueChange={setRvType}>
                <SelectTrigger id="rv-type" className="h-12 text-base">
                  <SelectValue placeholder="Select your RV type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(RV_TYPES).map((type) => (
                    <SelectItem key={type} value={type} className="text-base py-3">
                      <span className="flex items-center gap-2">
                        <span>{RV_TYPES[type].icon}</span>
                        <span>{type}</span>
                        <span className="text-sm text-gray-500">
                          ({RV_TYPES[type].minLength}-{RV_TYPES[type].maxLength} ft)
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* RV Length Input */}
            <div className="space-y-2">
              <Label htmlFor="rv-length" className="text-base font-semibold">
                RV Length (feet)
              </Label>
              <div className="relative">
                <Input
                  id="rv-length"
                  type="number"
                  min={rvType ? RV_TYPES[rvType].minLength : 15}
                  max={rvType ? RV_TYPES[rvType].maxLength : 45}
                  value={rvLength}
                  onChange={(e) => setRvLength(e.target.value)}
                  placeholder="Enter length"
                  className="h-12 text-base pr-12"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  ft
                </span>
              </div>
              {rvType && (
                <p className="text-sm text-gray-600">
                  Valid range: {RV_TYPES[rvType].minLength}-{RV_TYPES[rvType].maxLength} feet
                </p>
              )}
            </div>

            {/* Calculate Button */}
            <Button
              type="submit"
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 transition-all duration-300"
              disabled={!rvType || !rvLength}
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate My Quote
            </Button>
          </form>

          {/* Quote Display */}
          {showQuote && quote && !quote.error && (
            <div className="mt-8 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border-2 border-emerald-200 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-emerald-500 rounded-full p-2">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Your Estimated Price
                  </h3>
                  <p className="text-gray-700">
                    {quote.rvType} • {quote.length} ft
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 mb-4 shadow-sm">
                <div className="text-center">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 mb-2">
                    ${quote.price}
                  </div>
                  <p className="text-sm text-gray-600">
                    Price range for your RV: {quote.priceRange}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Includes Full-Service Exterior Care:
                </h4>
                <ul className="space-y-2">
                  {quote.services.map((service, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-emerald-600 font-bold mt-1">✓</span>
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-6 border-t border-emerald-200 space-y-4">
                <Button
                  onClick={handleBookNow}
                  className="w-full h-14 text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  🎯 Book This Service Now!
                </Button>
                <p className="text-center text-gray-600 text-sm">
                  Or call us directly at{' '}
                  <a href="tel:5743225652" className="text-emerald-700 font-bold hover:text-emerald-800 transition-colors">
                    (574) 322-5652
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {showQuote && quote && quote.error && (
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg animate-in fade-in slide-in-from-bottom-4 duration-300">
              <p className="text-red-800 font-medium">{quote.error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Form Dialog */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {bookingSubmitted ? '🎉 Booking Received!' : 'Complete Your Booking'}
            </DialogTitle>
            <DialogDescription className="text-base">
              {bookingSubmitted 
                ? "We'll call you shortly to confirm your appointment!"
                : `Book your ${quote?.rvType} detailing service for $${quote?.price}`
              }
            </DialogDescription>
          </DialogHeader>

          {bookingSubmitted ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">🎉 Booking Received!</h3>
              <p className="text-gray-700">
                Thank you, <strong>{bookingData.name}</strong>! Your booking request has been received.
              </p>
              <p className="text-gray-700">
                We'll call you at <strong>{bookingData.phone}</strong> within the next few hours to confirm your appointment for <strong>{bookingData.date ? format(bookingData.date, 'MMMM d, yyyy') : 'your preferred date'}</strong>.
              </p>
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-amber-900 font-semibold">📞 Expect Our Call Soon!</p>
                <p className="text-amber-800 text-sm mt-1">
                  We'll reach out shortly to confirm all the details and answer any questions.
                </p>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Questions? Call us now: <a href="tel:5743225652" className="text-orange-600 font-bold">(574) 322-5652</a>
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleBookingSubmit} className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Your Contact Information
                </h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={bookingData.name}
                      onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                      placeholder="John Smith"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={bookingData.phone}
                      onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                      placeholder="(574) 555-0123"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={bookingData.email}
                    onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Service Location */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Service Location (We Come To You!)
                </h4>
                
                <div>
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    required
                    value={bookingData.address}
                    onChange={(e) => setBookingData({...bookingData, address: e.target.value})}
                    placeholder="123 Main Street"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    required
                    value={bookingData.city}
                    onChange={(e) => setBookingData({...bookingData, city: e.target.value})}
                    placeholder="Akron, IN"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Date & Time */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Preferred Date & Time
                </h4>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Preferred Date *</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-1 h-10"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {bookingData.date ? format(bookingData.date, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[9999]" align="start" sideOffset={5}>
                        <Calendar
                          mode="single"
                          selected={bookingData.date}
                          onSelect={(date) => setBookingData({...bookingData, date})}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="rounded-md border"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="timeSlot">Preferred Time *</Label>
                    <Select
                      value={bookingData.timeSlot}
                      onValueChange={(value) => setBookingData({...bookingData, timeSlot: value})}
                      required
                    >
                      <SelectTrigger id="timeSlot" className="mt-1">
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            {slot}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              <div className="space-y-2">
                <Label htmlFor="notes">Special Requests or Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                  placeholder="Any specific areas of concern? Gate code? Best way to reach you?"
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Service Summary */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Service Summary</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Service:</strong> Mobile RV Detailing</p>
                  <p><strong>RV Type:</strong> {quote?.rvType} • {quote?.length} ft</p>
                  <p><strong>Estimated Price:</strong> <span className="text-xl font-bold text-orange-600">${quote?.price}</span></p>
                  <p className="text-xs text-gray-600 mt-2">Final price confirmed upon inspection. Payment due after service completion.</p>
                </div>
              </div>

              {/* Error Message */}
              {bookingError && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{bookingError}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBookingForm(false)}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                  disabled={isSubmitting || !bookingData.date || !bookingData.timeSlot}
                >
                  {isSubmitting ? 'Submitting...' : '✓ Confirm Booking'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
